import {DateTime} from "luxon";
import {Expose} from "class-transformer";
import {Entity} from "./entity";
import {typeInfo} from "../../decorators/type-info.decorator";
import {viewComponent} from "../../decorators/view-component.decorator";

/**
 * KnowledgeObject — metadata-only frontend entity.
 *
 * Mirrors the backend `KnowledgeObject::jsonSerialize()` shape (PRD-064).
 * Intentionally has NO `content` property — content is loaded only by the
 * detail query in PRD-067.
 */
export class KnowledgeObject extends Entity<KnowledgeObject> {

    private _type: string = `whiteboard`;

    @Expose()
    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => String})
    @viewComponent("ha-string-viewer")
    public get type(): string {
        return this._type;
    }

    public set type(value: string) {
        this._type = value;
        this._setOriginalValue(`type`, value);
    }

    private _title: string = ``;

    @Expose()
    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => String})
    @viewComponent("ha-string-viewer")
    public get title(): string {
        return this._title;
    }

    public set title(value: string) {
        this._title = value;
        this._setOriginalValue(`title`, value);
    }

    private _payloadVersion: number = 0;

    public get payloadVersion(): number {
        return this._payloadVersion;
    }

    public set payloadVersion(value: number) {
        this._payloadVersion = value;
        this._setOriginalValue(`payloadVersion`, value);
    }

    private _version: number = 0;

    public get version(): number {
        return this._version;
    }

    public set version(value: number) {
        this._version = value;
        this._setOriginalValue(`version`, value);
    }

    private _creatorUid: string | null = null;

    public get creatorUid(): string | null {
        return this._creatorUid;
    }

    public set creatorUid(value: string | null) {
        this._creatorUid = value;
        this._setOriginalValue(`creatorUid`, value);
    }

    private _lastEditorUid: string | null = null;

    public get lastEditorUid(): string | null {
        return this._lastEditorUid;
    }

    public set lastEditorUid(value: string | null) {
        this._lastEditorUid = value;
        this._setOriginalValue(`lastEditorUid`, value);
    }

    private _updatedAt: DateTime = DateTime.utc();

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => DateTime})
    @viewComponent("ha-date-viewer")
    public get updatedAt(): DateTime {
        return this._updatedAt;
    }

    public set updatedAt(value: DateTime) {
        this._updatedAt = value;
        this._setOriginalValue(`updatedAt`, value);
    }

    private _deletedAt: DateTime | null = null;

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => DateTime})
    @viewComponent("ha-date-viewer")
    public get deletedAt(): DateTime | null {
        return this._deletedAt;
    }

    public set deletedAt(value: DateTime | null) {
        this._deletedAt = value;
    }

    public get isDeleted(): boolean {
        return this._deletedAt instanceof DateTime;
    }

    private _lockedAt: string | null = null;

    /** When the current edit lock was taken, or `null` when nobody holds it.
     *  Server-owned, formatted by the backend serializer — see `Lockable`'s
     *  backend doc comment. Deliberately not refreshed on re-acquiring one's
     *  own lock: it is the age signal a colleague reads before deciding
     *  whether to take the object over. */
    public get lockedAt(): string | null {
        return this._lockedAt;
    }

    public set lockedAt(value: string | null) {
        this._lockedAt = value;
        this._setOriginalValue(`lockedAt`, value);
    }

    private _lockedBy: string | null = null;

    /** The uid (TRANSPORT form, 22-char ShortId) of whoever holds the edit
     *  lock, or `null` when nobody does. Passed back verbatim as
     *  `expectedHolderUid` when taking a lock over — see `deriveLocked()`'s
     *  own doc comment for the "mind the polarity" note this pairs with. */
    public get lockedBy(): string | null {
        return this._lockedBy;
    }

    public set lockedBy(value: string | null) {
        this._lockedBy = value;
        this._setOriginalValue(`lockedBy`, value);
    }

    private _content: { payload: unknown[] } | undefined = undefined;

    // Optional content (whiteboard payload). Populated only when the query was
    // sent with includes: ['content'] (PRD-076). Mirrors KnowledgeObjectDTO.content.
    public get content(): { payload: unknown[] } | undefined {
        return this._content;
    }

    public set content(value: { payload: unknown[] } | undefined) {
        this._content = value;
        this._setOriginalValue(`content`, value);
    }

    public toString(): string {
        return this.title;
    }
}
